import type { Asset, WarrantyStatus } from '@/types/storageInventory';

/**
 * Parses a YYYY-MM-DD date string into a Date object at midnight local time.
 * Returns null if the string is not a valid date.
 */
function parseDate(dateStr: string): Date | null {
  const parts = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (parts) {
    const date = new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
    if (!isNaN(date.getTime())) {
      return date;
    }
  }
  // Fallback for other date formats
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * Gets today's date at midnight local time.
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/**
 * Determines the warranty status based on a support end date.
 *
 * - "expired": support_end_date is before today
 * - "expiring-soon": support_end_date is between today and today+30 days (inclusive)
 * - "valid": support_end_date is more than 30 days from today
 * - "unknown": no support_end_date provided (null, undefined, or empty string)
 */
export function getWarrantyStatus(supportEndDate: string | null | undefined): WarrantyStatus {
  if (!supportEndDate || supportEndDate.trim() === '') {
    return 'unknown';
  }

  const endDate = parseDate(supportEndDate);
  if (!endDate) {
    return 'unknown';
  }

  const today = getToday();

  const thirtyDaysFromNow = new Date(today);
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

  if (endDate < today) {
    return 'expired';
  }

  if (endDate <= thirtyDaysFromNow) {
    return 'expiring-soon';
  }

  return 'valid';
}

/**
 * Filters assets by warranty window.
 *
 * - "expired": assets whose support_end_date is before today
 * - "30"/"60"/"90": assets whose support_end_date is between today (inclusive) and today+N days (inclusive)
 *
 * Assets without a support_end_date are always excluded from results.
 */
export function filterByWarranty(
  assets: Asset[],
  filter: 'expired' | '30' | '60' | '90'
): Asset[] {
  const today = getToday();

  return assets.filter((asset) => {
    // Check if the asset has a support_end_date field
    const supportEndDate = 'support_end_date' in asset ? (asset as { support_end_date?: string }).support_end_date : undefined;

    if (!supportEndDate || supportEndDate.trim() === '') {
      return false;
    }

    const endDate = parseDate(supportEndDate);
    if (!endDate) {
      return false;
    }

    if (filter === 'expired') {
      return endDate < today;
    }

    // For "30", "60", "90" filters: support_end_date between today (inclusive) and today+N days (inclusive)
    const days = parseInt(filter, 10);
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + days);

    return endDate >= today && endDate <= futureDate;
  });
}
